import { useState } from "react";
import { Mail, AlertTriangle, CheckCircle, XCircle, Loader2, Lightbulb, Sparkles, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { EmailResultsButton } from "@/components/EmailResultsButton";

interface SubjectLineAnalyzerProps {
  userEmail?: string | null;
  userName?: string | null;
  sessionToken?: string | null;
}

interface Trigger {
  word: string;
  category: string;
  severity: "low" | "medium" | "high";
  source?: "subject" | "body";
}

interface AnalysisResult {
  score: number;
  risk: "low" | "medium" | "high";
  triggers: Trigger[];
  suggestions: string[];
  improvedVersion: string;
}

type AnalyzeType = "subject" | "body" | "both";

export const SubjectLineAnalyzer = ({ userEmail, userName, sessionToken }: SubjectLineAnalyzerProps) => {
  const [subjectLine, setSubjectLine] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [analyzeType, setAnalyzeType] = useState<AnalyzeType>("subject");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);

  const sampleSubjects = [
    "🔥 URGENT: You Won $1000 FREE - Claim NOW!!!",
    "Quick question about your project",
    "LIMITED TIME: 90% OFF Everything - Act Fast!",
    "Meeting follow-up: Next steps for Q1 planning",
    "Congratulations! You've Been Selected as a Winner"
  ];

  const sampleBodies = [
    `Dear Valued Customer,

CONGRATULATIONS!!! You have been SELECTED as our LUCKY WINNER! 

Click HERE immediately to claim your FREE $5000 gift card! This offer expires in 24 HOURS!!!

Act NOW or you'll MISS OUT on this once-in-a-lifetime opportunity!

No purchase necessary. 100% GUARANTEED!`,
    `Hi there,

I wanted to follow up on our conversation last week about the Q1 marketing strategy.

I've attached the revised proposal with the changes we discussed. Let me know if you have any questions.

Best regards,
John`
  ];

  const loadSample = () => {
    if (analyzeType === "subject" || analyzeType === "both") {
      const randomSample = sampleSubjects[Math.floor(Math.random() * sampleSubjects.length)];
      setSubjectLine(randomSample);
    }
    if (analyzeType === "body" || analyzeType === "both") {
      const randomBody = sampleBodies[Math.floor(Math.random() * sampleBodies.length)];
      setEmailBody(randomBody);
    }
    setResult(null);
  };

  const canAnalyze = () => {
    if (analyzeType === "subject") return subjectLine.trim().length > 0;
    if (analyzeType === "body") return emailBody.trim().length > 0;
    if (analyzeType === "both") return subjectLine.trim().length > 0 && emailBody.trim().length > 0;
    return false;
  };

  const analyzeContent = async () => {
    if (!canAnalyze()) {
      if (analyzeType === "both") {
        toast.error("Please enter both subject line and email body");
      } else if (analyzeType === "subject") {
        toast.error("Please enter a subject line to analyze");
      } else {
        toast.error("Please enter email body content to analyze");
      }
      return;
    }

    setIsAnalyzing(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke("analyze-subject-line", {
        body: { 
          subjectLine: subjectLine.trim() || undefined,
          emailBody: emailBody.trim() || undefined,
          analyzeType 
        },
      });

      if (error) {
        throw error;
      }

      if (data.error) {
        throw new Error(data.error);
      }

      setResult(data);
    } catch (error) {
      console.error("Error analyzing content:", error);
      toast.error("Failed to analyze content. Please try again.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case "low": return "text-success";
      case "medium": return "text-warning";
      case "high": return "text-destructive";
      default: return "text-muted-foreground";
    }
  };

  const getRiskBg = (risk: string) => {
    switch (risk) {
      case "low": return "bg-success/10 border-success/20";
      case "medium": return "bg-warning/10 border-warning/20";
      case "high": return "bg-destructive/10 border-destructive/20";
      default: return "bg-muted";
    }
  };

  const getRiskIcon = (risk: string) => {
    switch (risk) {
      case "low": return <CheckCircle className="w-5 h-5 text-success" />;
      case "medium": return <AlertTriangle className="w-5 h-5 text-warning" />;
      case "high": return <XCircle className="w-5 h-5 text-destructive" />;
      default: return null;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "low": return "bg-success/10 text-success border-success/20";
      case "medium": return "bg-warning/10 text-warning border-warning/20";
      case "high": return "bg-destructive/10 text-destructive border-destructive/20";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const getAnalyzeTypeLabel = () => {
    switch (analyzeType) {
      case "subject": return "Subject Line";
      case "body": return "Email Body";
      case "both": return "Subject & Body";
    }
  };

  return (
    <div className="section-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Mail className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">
            Spam Trigger Analyzer
          </h3>
          <p className="text-sm text-muted-foreground">
            Analyze subject lines, email body, or both for spam triggers
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Analysis Type Selector */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            What to Analyze
          </label>
          <div className="flex gap-2">
            <Button
              variant={analyzeType === "subject" ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setAnalyzeType("subject");
                setResult(null);
              }}
              className="flex-1"
            >
              <Mail className="w-4 h-4 mr-1.5" />
              Subject Line
            </Button>
            <Button
              variant={analyzeType === "body" ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setAnalyzeType("body");
                setResult(null);
              }}
              className="flex-1"
            >
              <FileText className="w-4 h-4 mr-1.5" />
              Email Body
            </Button>
            <Button
              variant={analyzeType === "both" ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setAnalyzeType("both");
                setResult(null);
              }}
              className="flex-1"
            >
              <Sparkles className="w-4 h-4 mr-1.5" />
              Both
            </Button>
          </div>
        </div>

        {/* Subject Line Input */}
        {(analyzeType === "subject" || analyzeType === "both") && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-foreground">
                Email Subject Line
              </label>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadSample}
                className="text-xs text-primary hover:text-primary/80"
              >
                Load Sample
              </Button>
            </div>
            <Textarea
              value={subjectLine}
              onChange={(e) => {
                setSubjectLine(e.target.value);
                setResult(null);
              }}
              placeholder="Enter your email subject line here..."
              className="min-h-[60px] resize-none"
              maxLength={200}
            />
            <p className="text-xs text-muted-foreground mt-1 text-right">
              {subjectLine.length}/200 characters
            </p>
          </div>
        )}

        {/* Email Body Input */}
        {(analyzeType === "body" || analyzeType === "both") && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-foreground">
                Email Body Content
              </label>
              {analyzeType === "body" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadSample}
                  className="text-xs text-primary hover:text-primary/80"
                >
                  Load Sample
                </Button>
              )}
            </div>
            <Textarea
              value={emailBody}
              onChange={(e) => {
                setEmailBody(e.target.value);
                setResult(null);
              }}
              placeholder="Paste your email body content here (plain text or HTML)..."
              className="min-h-[150px] resize-none font-mono text-sm"
              maxLength={10000}
            />
            <p className="text-xs text-muted-foreground mt-1 text-right">
              {emailBody.length}/10,000 characters
            </p>
          </div>
        )}

        <Button
          onClick={analyzeContent}
          disabled={isAnalyzing || !canAnalyze()}
          className="w-full"
        >
          {isAnalyzing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Analyze {getAnalyzeTypeLabel()}
            </>
          )}
        </Button>

        {result && (
          <div className="space-y-4 pt-4 border-t border-border">
            {/* Email Results Button */}
            <div className="flex justify-end">
              <EmailResultsButton
                toolId="subject-analyzer"
                toolName="Spam Trigger Analyzer"
                results={result}
                userEmail={userEmail || null}
                userName={userName || null}
                sessionToken={sessionToken || null}
              />
            </div>

            {/* Score Card */}
            <div className={`rounded-xl border p-4 ${getRiskBg(result.risk)}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getRiskIcon(result.risk)}
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Spam Risk Score
                    </p>
                    <p className={`text-2xl font-bold ${getRiskColor(result.risk)}`}>
                      {result.score}/100
                    </p>
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium uppercase ${getRiskColor(result.risk)} bg-background/50`}>
                  {result.risk} Risk
                </div>
              </div>
            </div>

            {/* Triggers Found */}
            {result.triggers.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-warning" />
                  Spam Triggers Detected ({result.triggers.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {result.triggers.map((trigger, index) => (
                    <span
                      key={index}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${getSeverityColor(trigger.severity)}`}
                    >
                      "{trigger.word}"
                      <span className="opacity-70">• {trigger.category}</span>
                      {trigger.source && analyzeType === "both" && (
                        <span className="opacity-50">({trigger.source})</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Suggestions */}
            {result.suggestions.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-primary" />
                  Recommendations
                </h4>
                <ul className="space-y-1.5">
                  {result.suggestions.map((suggestion, index) => (
                    <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Improved Version */}
            {result.improvedVersion && (analyzeType === "subject" || analyzeType === "both") && (
              <div className="bg-success/5 border border-success/20 rounded-xl p-4">
                <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-success" />
                  Suggested Improvement
                </h4>
                <p className="text-sm text-foreground bg-background/50 rounded-lg p-3 border border-border">
                  "{result.improvedVersion}"
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 text-xs text-success hover:text-success/80"
                  onClick={() => {
                    setSubjectLine(result.improvedVersion);
                    setResult(null);
                    toast.success("Improved version loaded - analyze again to verify");
                  }}
                >
                  Use This Version
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
